import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { API_BASE } from "../config";
import { authHeaders } from "../utils/authHeaders";

export default function Shareholders() {
  const [shareholders, setShareholders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [panchayats, setPanchayats] = useState([]);
  const [villages, setVillages] = useState([]);

  const [geo, setGeo] = useState({
    state_id: "",
    district_id: "",
    block_id: "",
    panchayat_id: "",
    village_id: "",
  });

  const [form, setForm] = useState({
    folio_code: "",
    tally_code: "",
    name: "",
    calling_name: "",
    phone: "",
    village: "",
    panchayat: "",
    block: "",
    district: "",
    state: "",
    antyodaya_code: "",
    agent_id: "",
  });

  const loadAgents = async () => {
    const res = await fetch(`${API_BASE}/agents`, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to load agents");
    }

    setAgents(Array.isArray(data) ? data : []);
  };

  const loadShareholders = async () => {
    const res = await fetch(`${API_BASE}/shareholders`, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to load shareholders");
    }

    setShareholders(Array.isArray(data.data) ? data.data : []);
  };

  const loadStates = async () => {
    const res = await fetch(`${API_BASE}/fpo-geo/states`, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to load states");
    }

    setStates(Array.isArray(data.data) ? data.data : []);
  };

  const loadDistricts = async (stateId) => {
    setDistricts([]);
    setBlocks([]);
    setPanchayats([]);
    setVillages([]);

    if (!stateId) return;

    const res = await fetch(`${API_BASE}/fpo-geo/districts?state_id=${stateId}`, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to load districts");
    }

    setDistricts(Array.isArray(data.data) ? data.data : []);
  };

  const loadBlocks = async (districtId) => {
    setBlocks([]);
    setPanchayats([]);
    setVillages([]);

    if (!districtId) return;

    const res = await fetch(`${API_BASE}/fpo-geo/blocks?district_id=${districtId}`, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to load blocks");
    }

    setBlocks(Array.isArray(data.data) ? data.data : []);
  };

  const loadPanchayats = async (blockId) => {
    setPanchayats([]);
    setVillages([]);

    if (!blockId) return;

    const res = await fetch(`${API_BASE}/fpo-geo/panchayats?block_id=${blockId}`, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to load panchayats");
    }

    setPanchayats(Array.isArray(data.data) ? data.data : []);
  };

  const loadVillages = async (panchayatId) => {
    setVillages([]);

    if (!panchayatId) return;

    const res = await fetch(`${API_BASE}/fpo-geo/villages?panchayat_id=${panchayatId}`, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to load villages");
    }

    setVillages(Array.isArray(data.data) ? data.data : []);
  };

  const loadAll = async () => {
    setLoading(true);
    setMessage("");

    try {
      await loadAgents();
      await loadShareholders();
      await loadStates();
    } catch (err) {
      console.error("Load shareholders error:", err);
      setMessage(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleStateChange = async (e) => {
    const stateId = e.target.value;
    const selected = states.find((s) => String(s.id) === String(stateId));

    setGeo({
      state_id: stateId,
      district_id: "",
      block_id: "",
      panchayat_id: "",
      village_id: "",
    });

    setForm((prev) => ({
      ...prev,
      state: selected?.name || "",
      district: "",
      block: "",
      panchayat: "",
      village: "",
    }));

    await loadDistricts(stateId);
  };

  const handleDistrictChange = async (e) => {
    const districtId = e.target.value;
    const selected = districts.find((d) => String(d.id) === String(districtId));

    setGeo((prev) => ({
      ...prev,
      district_id: districtId,
      block_id: "",
      panchayat_id: "",
      village_id: "",
    }));

    setForm((prev) => ({
      ...prev,
      district: selected?.name || "",
      block: "",
      panchayat: "",
      village: "",
    }));

    await loadBlocks(districtId);
  };

  const handleBlockChange = async (e) => {
    const blockId = e.target.value;
    const selected = blocks.find((b) => String(b.id) === String(blockId));

    setGeo((prev) => ({
      ...prev,
      block_id: blockId,
      panchayat_id: "",
      village_id: "",
    }));

    setForm((prev) => ({
      ...prev,
      block: selected?.name || "",
      panchayat: "",
      village: "",
    }));

    await loadPanchayats(blockId);
  };

  const handlePanchayatChange = async (e) => {
    const panchayatId = e.target.value;
    const selected = panchayats.find((p) => String(p.id) === String(panchayatId));

    setGeo((prev) => ({
      ...prev,
      panchayat_id: panchayatId,
      village_id: "",
    }));

    setForm((prev) => ({
      ...prev,
      panchayat: selected?.name || "",
      village: "",
    }));

    await loadVillages(panchayatId);
  };

  const handleVillageChange = (e) => {
    const villageId = e.target.value;
    const selected = villages.find((v) => String(v.id) === String(villageId));

    setGeo((prev) => ({
      ...prev,
      village_id: villageId,
    }));

    setForm((prev) => ({
      ...prev,
      village: selected?.name || "",
      antyodaya_code: selected?.code || prev.antyodaya_code || "",
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!form.name.trim()) {
      setMessage("Shareholder name is required");
      return;
    }

    try {
      const payload = {
        ...form,
        tally_code: form.tally_code.trim() || null,
        agent_id: form.agent_id ? Number(form.agent_id) : null,
      };

      const res = await fetch(`${API_BASE}/shareholders`, {
        method: "POST",
        headers: authHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to create shareholder");
      }

      setMessage("Shareholder created successfully");

      setForm({
        folio_code: "",
        tally_code: "",
        name: "",
        phone: "",
        village: "",
        panchayat: "",
        block: "",
        district: "",
        state: "",
        antyodaya_code: "",
        agent_id: "",
      });

      setGeo({
        state_id: "",
        district_id: "",
        block_id: "",
        panchayat_id: "",
        village_id: "",
      });

      setDistricts([]);
      setBlocks([]);
      setPanchayats([]);
      setVillages([]);

      await loadShareholders();
    } catch (err) {
      console.error("Create shareholder error:", err);
      setMessage(err.message || "Failed to create shareholder");
    }
  };

  const handleAssignAgent = async (shareholderId, agentId) => {
    setMessage("");

    try {
      const res = await fetch(
        `${API_BASE}/shareholders/${shareholderId}/assign-agent`,
        {
          method: "PUT",
          headers: authHeaders({
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({
            agent_id: agentId ? Number(agentId) : null,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to assign agent");
      }

      setMessage("Agent mapping updated");
      await loadShareholders();
    } catch (err) {
      console.error("Assign agent error:", err);
      setMessage(err.message || "Failed to assign agent");
    }
  };

  const handleStatus = async (shareholderId, isActive) => {
    setMessage("");

    try {
      const res = await fetch(`${API_BASE}/shareholders/${shareholderId}/status`, {
        method: "PUT",
        headers: authHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          is_active: isActive,
        }),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to update status");
      }

      setMessage(isActive ? "Shareholder allowed" : "Shareholder disallowed");
      await loadShareholders();
    } catch (err) {
      console.error("Update status error:", err);
      setMessage(err.message || "Failed to update status");
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();

    setMessage("");
    setUploadSummary(null);

    if (!uploadFile) {
      setMessage("Please select shareholder Excel file");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", uploadFile);

      const res = await fetch(`${API_BASE}/shareholders/upload`, {
        method: "POST",
        headers: authHeaders(),
        body: formData,
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        setUploadSummary(data.summary || null);
        throw new Error(data.message || "Shareholder upload failed");
      }

      setMessage(data.message || "Shareholders uploaded successfully");
      setUploadSummary(data.summary || null);

      setUploadFile(null);

      const input = document.getElementById("shareholder-upload-file");
      if (input) input.value = "";

      await loadShareholders();
    } catch (err) {
      console.error("Shareholder upload error:", err);
      setMessage(err.message || "Shareholder upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />

      <div style={{ padding: 24, width: "100%" }}>
        <h2>Shareholders</h2>

        <p style={{ color: "#555" }}>
          Add shareholder list, map shareholders to agents, and allow/disallow their app visibility.
        </p>

        {message && (
          <div
            style={{
              marginBottom: 16,
              padding: 10,
              background: "#f5f5f5",
              border: "1px solid #ddd",
            }}
          >
            {message}
          </div>
        )}

        <div
          style={{
            marginBottom: 24,
            padding: 16,
            border: "1px solid #ddd",
            background: "#fafafa",
          }}
        >
          <h3>Bulk Upload Shareholders</h3>

          <p style={{ color: "#555" }}>
            Upload shareholders for this FPO. Geography must be already mapped to this FPO.
            If any row has an error, the full file will be rejected.
          </p>

          <div
            style={{
              marginBottom: 12,
              padding: 12,
              background: "#fff",
              border: "1px solid #eee",
            }}
          >
            <strong>Required Excel Format</strong>

            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table
                border="1"
                cellPadding="8"
                cellSpacing="0"
                style={{ borderCollapse: "collapse", width: "100%" }}
              >
                <thead>
                  <tr style={{ background: "#f0f0f0" }}>
                    <th>Folio Code</th>
                    <th>Name</th>
                    <th>Calling Name</th>
                    <th>Phone</th>
                    <th>State Code</th>
                    <th>District Code</th>
                    <th>Block Code</th>
                    <th>Panchayat Code</th>
                    <th>Village Code</th>
                    <th>Agent ID</th>
                    <th>Tally Code (optional)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>SH-001</td>
                    <td>Saraswati Devi</td>
                    <td>Munni Devi</td>
                    <td>9000000000</td>
                    <td>10</td>
                    <td>225</td>
                    <td>1583</td>
                    <td>123456</td>
                    <td>123456789</td>
                    <td>1</td>
                    <td>TC-001</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p style={{ color: "#777", marginTop: 8 }}>
              Mandatory: Folio Code, Name, State Code, District Code, Block Code,
              Panchayat Code, Village Code, Agent ID. Calling Name, Phone and Tally Code are optional.
              Nonblank Tally Codes must be unique within this FPO; case is preserved.
            </p>
          </div>

          <form
            onSubmit={handleBulkUpload}
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              id="shareholder-upload-file"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setUploadFile(e.target.files[0] || null)}
            />

            <button
              type="submit"
              disabled={uploading}
              style={{
                padding: "8px 14px",
                background: uploading ? "#999" : "#1976D2",
                color: "#fff",
                border: "none",
                cursor: uploading ? "not-allowed" : "pointer",
              }}
            >
              {uploading ? "Uploading..." : "Upload Shareholders"}
            </button>
          </form>

          {uploadSummary && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: "#fff",
                border: "1px solid #ddd",
              }}
            >
              <h4>Upload Summary</h4>

              <table
                border="1"
                cellPadding="8"
                cellSpacing="0"
                style={{ borderCollapse: "collapse", minWidth: 400 }}
              >
                <tbody>
                  <tr>
                    <td>Total Rows</td>
                    <td>{uploadSummary.total_rows ?? 0}</td>
                  </tr>
                  <tr>
                    <td>Processed</td>
                    <td>{uploadSummary.processed ?? 0}</td>
                  </tr>
                  <tr>
                    <td>Inserted</td>
                    <td>{uploadSummary.inserted ?? 0}</td>
                  </tr>
                  <tr>
                    <td>Skipped</td>
                    <td>{uploadSummary.skipped ?? 0}</td>
                  </tr>
                </tbody>
              </table>

              {Array.isArray(uploadSummary.errors) &&
                uploadSummary.errors.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <h4>Errors</h4>

                    <table
                      border="1"
                      cellPadding="8"
                      cellSpacing="0"
                      style={{ borderCollapse: "collapse", width: "100%" }}
                    >
                      <thead>
                        <tr style={{ background: "#f0f0f0" }}>
                          <th>Row</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadSummary.errors.map((err, index) => (
                          <tr key={index}>
                            <td>{err.row}</td>
                            <td>{err.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}
        </div>

        <form
          onSubmit={handleCreate}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(140px, 1fr))",
            gap: 10,
            alignItems: "end",
            marginBottom: 24,
          }}
        >
          <div>
            <label>Folio Code</label>
            <input
              name="folio_code"
              value={form.folio_code}
              onChange={handleChange}
              placeholder="Unique within FPO"
              style={{ width: "100%", padding: 8 }}
            />
          </div>

          <div>
            <label htmlFor="shareholder-tally-code">Tally Code (optional)</label>
            <input
              id="shareholder-tally-code"
              name="tally_code"
              value={form.tally_code}
              onChange={handleChange}
              placeholder="Optional; unique within FPO"
              style={{ width: "100%", padding: 8 }}
            />
          </div>

          <div>
            <label>Name *</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Shareholder Name"
              style={{ width: "100%", padding: 8 }}
            />
          </div>

<div>
  <label>Calling Name</label>
  <input
    name="calling_name"
    value={form.calling_name}
    onChange={handleChange}
    placeholder="Local/common name"
    style={{ width: "100%", padding: 8 }}
  />
</div>

          <div>
            <label>Phone</label>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Phone"
              style={{ width: "100%", padding: 8 }}
            />
          </div>

          <div>
            <label>State</label>
            <select
              value={geo.state_id}
              onChange={handleStateChange}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Select State</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.code ? `(${s.code})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>District</label>
            <select
              value={geo.district_id}
              onChange={handleDistrictChange}
              disabled={!geo.state_id}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Select District</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} {d.code ? `(${d.code})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Block</label>
            <select
              value={geo.block_id}
              onChange={handleBlockChange}
              disabled={!geo.district_id}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Select Block</option>
              {blocks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.code ? `(${b.code})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Panchayat</label>
            <select
              value={geo.panchayat_id}
              onChange={handlePanchayatChange}
              disabled={!geo.block_id}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Select Panchayat</option>
              {panchayats.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.code ? `(${p.code})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Village</label>
            <select
              value={geo.village_id}
              onChange={handleVillageChange}
              disabled={!geo.panchayat_id}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Select Village</option>
              {villages.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} {v.code ? `(${v.code})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Antyodaya Code</label>
            <input
              name="antyodaya_code"
              value={form.antyodaya_code}
              onChange={handleChange}
              placeholder="Numeric code"
              style={{ width: "100%", padding: 8 }}
            />
          </div>

          <div>
            <label>Agent</label>
            <select
              name="agent_id"
              value={form.agent_id}
              onChange={handleChange}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Unassigned</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} #{agent.id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <button
              type="submit"
              style={{
                padding: "9px 14px",
                background: "#1976D2",
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
            >
              Add Shareholder
            </button>
          </div>
        </form>

        <h3>Shareholder List</h3>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <table
            border="1"
            cellPadding="8"
            cellSpacing="0"
            style={{ borderCollapse: "collapse", width: "100%" }}
          >
            <thead>
              <tr style={{ background: "#f0f0f0" }}>
                <th>ID</th>
                <th>Folio</th>
                <th>Name</th>
                <th>Calling Name</th>
                <th>Phone</th>
                <th>Village</th>
                <th>Antyodaya Code</th>
                <th>Agent</th>
                <th>Status</th>
                <th>Assign Agent</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {shareholders.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center" }}>
                    No shareholder found
                  </td>
                </tr>
              ) : (
                shareholders.map((sh) => (
                  <tr key={sh.id}>
                    <td>{sh.id}</td>
                    <td>{sh.folio_code || "-"}</td>
                    <td>{sh.name}</td>
                    <td>{sh.calling_name || "-"}</td>
                    <td>{sh.phone || "-"}</td>
                    <td>{sh.village || "-"}</td>
                    <td>{sh.antyodaya_code || "-"}</td>
                    <td>{sh.agent_name || "Unassigned"}</td>
                    <td>{sh.is_active ? "Allowed" : "Disallowed"}</td>
                    <td>
                      <select
                        value={sh.agent_id || ""}
                        onChange={(e) => handleAssignAgent(sh.id, e.target.value)}
                        style={{ padding: 6, minWidth: 150 }}
                      >
                        <option value="">Unassigned</option>
                        {agents.map((agent) => (
                          <option key={agent.id} value={agent.id}>
                            {agent.name} #{agent.id}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {sh.is_active ? (
                        <button
                          onClick={() => handleStatus(sh.id, false)}
                          style={{
                            padding: "6px 10px",
                            background: "#b00020",
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          Disallow
                        </button>
                      ) : (
                        <button
                          onClick={() => handleStatus(sh.id, true)}
                          style={{
                            padding: "6px 10px",
                            background: "#2e7d32",
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          Allow
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}